import { IconMailOff } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useEmailSkipReasonLabel } from "@/hooks/useEmailSkipReasonLabel";
import { emailSkipReasonKey } from "@/lib/utils/emailSkipReason";
import { EmailStatus } from "@/types/enums/EmailStatus";

interface SkipReasonBlockProps {
  emailStatus?: EmailStatus | string | null;
  skipReason?: string | null;
}

/**
 * Sent-mail detail: this row was never handed to SES, and why.
 *
 * A SKIPPED row carries no subject, no body and no delivery events — the
 * dialog used to open on three empty boxes and "No events available", which
 * reads as broken rather than as withheld. This block is the first thing in
 * the left column for such a row. Send-time fact, not a delivery event —
 * hence a block of its own, like the attachment ones, and not a card in the
 * timeline. Renders nothing for every other status.
 */
export const SkipReasonBlock: React.FC<SkipReasonBlockProps> = ({
  emailStatus,
  skipReason,
}) => {
  const t = useTranslations("pEmailSent");
  const reasonLabel = useEmailSkipReasonLabel();

  if (emailStatus !== EmailStatus.SKIPPED) return null;

  // A known code gets its sentence. An unknown one is printed AS the code:
  // the tooltip surfaces need a full sentence ("Not sent – reason code x")
  // because the pill says nothing else, but after this block's own headline
  // and "Reason:" label that sentence would say "not sent" and "reason" twice.
  const reason =
    skipReason && emailSkipReasonKey(skipReason) === null
      ? skipReason
      : reasonLabel(skipReason);

  return (
    <div
      data-testid="skip-reason-block"
      className="flex flex-col gap-1 mb-4 border rounded-lg px-4 py-3 surface-secondary outline-quaternary"
    >
      <div className="flex items-center gap-2">
        <IconMailOff size={16} className="shrink-0 font-secondary" />
        <p className="body-2-medium">{t("skippedTitle")}</p>
      </div>
      {reason && (
        <p className="body-3-regular font-tertiary">
          {t("reason")}: {reason}
        </p>
      )}
    </div>
  );
};
